/**
 * 补录系统外老客户：只要求客户姓名，其余信息可暂缺。
 * 独立档案不会生成工程、合同或财务记录；所属分公司受服务端数据范围约束。
 */
import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { errMsg } from "../referral/refUtils";

const BRANCHES = [
  { id: "la", zh: "洛杉矶", en: "Los Angeles" },
  { id: "norcal", zh: "北加州", en: "Northern California" },
  { id: "sd", zh: "圣地亚哥", en: "San Diego" },
];

/** @param {Function} onSave 接收客户字段；请求失败时保留用户已填写的内容。 */
export default function PastCustomerCreateDialog({ open, onClose, onSave, branch, scopeBranch, zh }) {
  // 总部筛选了分公司时默认带入筛选值，但所属分公司仍允许明确调整或留空。
  const [form, setForm] = useState({ clientName: "", phone: "", email: "", address: "", branch: scopeBranch === "all" && branch !== "all" ? branch || "" : "" });
  const [busy, setBusy] = useState(false);
  // 保存后若响应或审计失败，保留同一标识重试，避免产生重复客户。
  const [idempotencyKey] = useState(() => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [error, setError] = useState("");
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.clientName.trim() || busy) return;
    setError("");
    setBusy(true);
    try {
      await onSave({ ...form, clientName: form.clientName.trim(), idempotencyKey });
      setForm({ clientName: "", phone: "", email: "", address: "", branch: "" });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <DialogContent closeLabel={zh ? "关闭" : "Close"} closeDisabled={busy} data-testid="asc-create-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "新增老客户" : "Add past customer"}</DialogTitle>
          <DialogDescription>{zh ? "先保存已有的客户资料；未关联项目、合同和收款也可以建档。" : "Save the information you have. A project, contract or payment is not required."}</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="asc-create-name">{zh ? "客户姓名 *" : "Customer name *"}</Label>
              <Input id="asc-create-name" data-testid="asc-create-name" value={form.clientName} onChange={(e) => set("clientName", e.target.value)} maxLength={100} required autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asc-create-phone">{zh ? "电话" : "Phone"}</Label>
              <Input id="asc-create-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asc-create-email">{zh ? "邮箱" : "Email"}</Label>
              <Input id="asc-create-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} maxLength={160} />
            </div>
            {scopeBranch === "all" && <div className="space-y-1.5">
              <Label htmlFor="asc-create-branch">{zh ? "所属分公司" : "Branch"}</Label>
              <Select value={form.branch || "unassigned"} onValueChange={(value) => set("branch", value === "unassigned" ? "" : value)}>
                <SelectTrigger id="asc-create-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">{zh ? "暂未确定" : "Not assigned"}</SelectItem>
                  {BRANCHES.map((item) => <SelectItem key={item.id} value={item.id}>{zh ? item.zh : item.en}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="asc-create-address">{zh ? "地址" : "Address"}</Label>
              <Input id="asc-create-address" value={form.address} onChange={(e) => set("address", e.target.value)} maxLength={300} />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="submit" data-testid="asc-create-submit" disabled={busy || !form.clientName.trim()}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{zh ? "保存客户" : "Save customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
